import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient, QueryClientProvider, environmentManager } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { ME_QUERY_KEY } from '@xver/hooks/useMeQuery';

import { ApplicationState } from '../store';
import { decodeJwtPayload } from '../utils/jwt';
import storage from '../utils/storage';

const PERSIST_KEY = 'xver:rq-cache';
const PERSIST_MAX_AGE = 1000 * 60 * 60 * 24;
const PERSIST_BUSTER = 'v2';
const ANON_BUSTER = `${PERSIST_BUSTER}:anon`;
let liveBuster = ANON_BUSTER;

let browserQueryClient: QueryClient | undefined = undefined;

function createQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 1000 * 60 * 5,
                gcTime: PERSIST_MAX_AGE,
            },
        },
    });
}

function getQueryClient() {
    if (environmentManager.isServer()) {
        return createQueryClient();
    }

    return !browserQueryClient ? (browserQueryClient = createQueryClient()) : browserQueryClient;
}

const persister =
    !environmentManager.isServer() && typeof window !== 'undefined'
        ? createAsyncStoragePersister({ storage: window.localStorage, key: PERSIST_KEY })
        : undefined;

export async function clearPersistedCache() {
    browserQueryClient?.clear();
    if (persister) {
        await persister.removeClient();
    }
}

export default function QueryProvider({ children }: PropsWithChildren) {
    const queryClient = getQueryClient();

    const userId = useSelector((state: ApplicationState) => state.auth.user?.id);

    // Scopes the persisted cache to one account. A buster mismatch makes
    // persistQueryClientRestore discard the whole stored cache, so a different
    // account can never hydrate the previous one's data. Seeded from the JWT
    // because the first restore runs before UserProvider populates Redux.
    // Signing in (or switching account) resets queries rather than clear()ing
    // them: this effect runs after the new page's queries have started, and
    // clear() destroys them under their mounted observers, which then never
    // settle. Signing out needs neither: logout and session expiry clear the
    // cache themselves. PersistQueryClientProvider keeps the options it
    // subscribed with, so saves after an in-app sign-in would carry the
    // signed-out buster; shouldDehydrateQuery persists nothing until a reload
    // subscribes with the account's own.
    const buster = `${PERSIST_BUSTER}:${
        userId ?? decodeJwtPayload(storage.get<string>('_zugang', ''))?.userID ?? 'anon'
    }`;
    liveBuster = buster;

    const previousBuster = useRef(buster);
    useEffect(() => {
        if (previousBuster.current === buster) return;
        previousBuster.current = buster;
        if (buster !== ANON_BUSTER) void browserQueryClient?.resetQueries();
    }, [buster]);

    if (!persister) {
        return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
    }

    return (
        <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
                persister,
                maxAge: PERSIST_MAX_AGE,
                buster,
                dehydrateOptions: {
                    shouldDehydrateQuery: (query) =>
                        buster === liveBuster &&
                        query.state.status === 'success' &&
                        query.queryKey[0] !== ME_QUERY_KEY[0],
                },
            }}
        >
            {children}
        </PersistQueryClientProvider>
    );
}
